/**
 * ConnectorsPanel — Data source and sink connector management.
 *
 * Existing OAuth-style connections remain visible here, and lane-2 import/key
 * connectors are surfaced as honest, actionable cards from real product
 * settings surfaces.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import api from '@/api/engine';
import type {
  ConversationImportResponse,
  CsvImportResponse,
  GedcomImportResponse,
  IndexedSourceResponse,
} from '@/api/types';
import { isTauriDesktopRuntime } from '@/utils/workbenchVertical';
import { API_V1 } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';
import { MacSessionReadbackCard } from '@/components/settings/panels/MacSessionReadbackCard';

interface Connection {
  source_type: string;
  connection_purpose: string;
  status: string;
  connected_account_name?: string;
  connected_at?: string;
  category?: string;
}

interface SourceCatalogEntry {
  slug: string;
  name: string;
  category: string;
  auth_type: string;
  status: string;
}

interface EnabledSourceEntry {
  slug: string;
  category: string;
  connection_status: string;
  external_username?: string | null;
  last_sync_at?: string | null;
}

const CONNECTOR_META: Record<string, { icon: string; label: string; category: string }> = {
  twitter: { icon: 'X', label: 'Twitter / X', category: 'Creative' },
  gmail: { icon: '📧', label: 'Gmail', category: 'Knowledge' },
  google_drive: { icon: '📁', label: 'Google Drive', category: 'Knowledge' },
  youtube: { icon: '▶', label: 'YouTube', category: 'Knowledge' },
  notebooklm: { icon: '📓', label: 'NotebookLM', category: 'Knowledge' },
  file_upload: { icon: '📁', label: 'File Upload', category: 'Knowledge' },
  pinterest: { icon: '📌', label: 'Pinterest', category: 'Creative' },
  fitbit: { icon: '⌚', label: 'Fitbit', category: 'Health' },
  oura: { icon: '💍', label: 'Oura Ring', category: 'Health' },
  familysearch: { icon: '🌳', label: 'FamilySearch', category: 'Knowledge' },
  telegram: { icon: '✈', label: 'Telegram', category: 'Chat' },
  whatsapp: { icon: '💬', label: 'WhatsApp', category: 'Chat' },
};

const CATEGORY_ORDER = ['Knowledge', 'Creative', 'Health', 'Chat'];
const FIELD_CLASSNAME =
  'mt-2 w-full rounded border border-gray-700 bg-black/20 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-[#39FF14]/50 focus:outline-none';

function summarizeIndexResult(result: IndexedSourceResponse): string {
  return `${result.total_indexed} indexed, ${result.new_items} new, ${result.updated_items} updated.`;
}

function summarizeCsvResult(result: CsvImportResponse): string {
  const formatLabel = result.format_detected ? ` Format: ${result.format_detected}.` : '';
  return `${result.rows_imported} rows imported, ${result.rows_skipped} skipped.${formatLabel}`;
}

function summarizeGedcomResult(result: GedcomImportResponse): string {
  return `${result.persons_imported} people and ${result.relationships_imported} relationships imported.`;
}

function summarizeConversationResult(result: ConversationImportResponse): string {
  return `${result.turns_parsed} turns parsed into ${result.chunks_created} Codex chunks.`;
}

async function parseJsonFile(file: File): Promise<Record<string, unknown> | unknown[]> {
  const raw = await file.text();
  try {
    return JSON.parse(raw) as Record<string, unknown> | unknown[];
  } catch {
    throw new Error('This export is not valid JSON.');
  }
}

async function upsertVaultEntry(keyType: string, label: string, value: string): Promise<void> {
  const existingEntries = await api.vault.listEntries();
  const existing = existingEntries.entries.find(
    (entry) => entry.key_type === keyType && entry.is_active
  );

  if (existing) {
    await api.vault.updateEntry(existing.id, {
      label,
      value,
      is_active: true,
    });
    return;
  }

  await api.vault.createEntry({
    key_type: keyType,
    label,
    value,
  });
}

function ImportStatus({
  error,
  success,
}: {
  error: string | null;
  success: string | null;
}) {
  if (error) {
    return (
      <div className="mt-3 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
        {error}
      </div>
    );
  }

  if (success) {
    return (
      <div className="mt-3 rounded border border-[#39FF14]/20 bg-[#39FF14]/10 px-3 py-2 text-xs text-[#d7ffd0]">
        {success}
      </div>
    );
  }

  return null;
}

function ImportCardShell({
  icon,
  title,
  description,
  badge,
  footer,
  children,
}: {
  icon: string;
  title: string;
  description: string;
  badge?: string;
  footer?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded border border-gray-800 bg-gray-950/40 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="w-6 flex-shrink-0 text-center text-lg">{icon}</span>
          <div>
            <div className="text-sm font-mono text-gray-200">{title}</div>
            <p className="mt-1 text-xs leading-5 text-gray-400">{description}</p>
          </div>
        </div>
        {badge ? (
          <span className="rounded border border-gray-700 px-2 py-1 text-[9px] font-mono uppercase tracking-[0.15em] text-gray-400">
            {badge}
          </span>
        ) : null}
      </div>
      {children}
      {footer ? <p className="mt-3 text-[11px] leading-5 text-gray-500">{footer}</p> : null}
    </div>
  );
}

function FileImportCard({
  icon,
  title,
  description,
  accept,
  buttonLabel,
  badge,
  footer,
  onImport,
  children,
  testId,
}: {
  icon: string;
  title: string;
  description: string;
  accept: string;
  buttonLabel: string;
  badge?: string;
  footer?: string;
  onImport: (file: File) => Promise<string>;
  children?: React.ReactNode;
  testId: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleFileSelected = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;

    setBusy(true);
    setError(null);
    setSuccess(null);
    setFileName(file.name);

    try {
      const message = await onImport(file);
      setSuccess(message);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ImportCardShell
      icon={icon}
      title={title}
      description={description}
      badge={badge}
      footer={footer}
    >
      {children}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="rounded border border-[#39FF14]/30 bg-[#39FF14]/10 px-3 py-2 text-xs font-mono text-[#39FF14] transition-colors hover:bg-[#39FF14]/20 disabled:opacity-50"
        >
          {busy ? 'Working...' : buttonLabel}
        </button>
        {fileName ? <span className="text-xs text-gray-500">{fileName}</span> : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={testId}
        onChange={(event) => {
          void handleFileSelected(event.target.files);
          event.currentTarget.value = '';
        }}
      />
      <ImportStatus error={error} success={success} />
    </ImportCardShell>
  );
}

function CsvImportCard() {
  const [formatOverride, setFormatOverride] = useState('');

  return (
    <FileImportCard
      icon="🧮"
      title="CSV Import"
      description="Upload a CSV directly into the import pipeline."
      accept=".csv,text/csv"
      buttonLabel="Upload CSV"
      testId="csv-import-input"
      onImport={async (file) => {
        const result = await api.imports.csv(file, formatOverride.trim() || undefined);
        return summarizeCsvResult(result);
      }}
      footer="Optional format override is passed directly to the backend if you know the target schema."
    >
      <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-gray-500">
        Format override
        <input
          value={formatOverride}
          onChange={(event) => setFormatOverride(event.target.value)}
          className={FIELD_CLASSNAME}
          placeholder="Optional, e.g. contacts"
        />
      </label>
    </FileImportCard>
  );
}

async function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text();
  }

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Could not read the selected file.'));
    reader.readAsText(file);
  });
}

function ConversationImportCard() {
  return (
    <FileImportCard
      icon="💬"
      title="Conversation Import"
      description="Bring in exported AI chat transcripts from Claude, ChatGPT, Perplexity, Grok, or generic markdown/text."
      accept=".json,.md,.txt"
      buttonLabel="Import Conversation"
      testId="conversation-import-input"
      onImport={async (file) => {
        const result = await api.conversations.import({
          file_content: await readFileText(file),
          filename: file.name,
        });
        return summarizeConversationResult(result);
      }}
      footer="Accepted formats: .json, .md, and .txt exports."
    />
  );
}

function ReadwiseImportCard() {
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savedKeyPresent, setSavedKeyPresent] = useState(false);

  const loadSavedKeyState = useCallback(async () => {
    try {
      const entries = await api.vault.listEntries();
      setSavedKeyPresent(
        entries.entries.some(
          (entry) => entry.key_type === 'readwise_api_token' && entry.is_active
        )
      );
    } catch {
      setSavedKeyPresent(false);
    }
  }, []);

  useEffect(() => {
    void loadSavedKeyState();
  }, [loadSavedKeyState]);

  const handleIndex = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed && !savedKeyPresent) {
      setError('Enter a Readwise API token first, or use an existing saved token.');
      setSuccess(null);
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      if (trimmed) {
        await upsertVaultEntry('readwise_api_token', 'Readwise API token', trimmed);
        setSavedKeyPresent(true);
        setApiKey('');
      }
      const result = await api.imports.readwise({});
      setSuccess(`Readwise indexed. ${summarizeIndexResult(result)}`);
    } catch (indexError) {
      setError(indexError instanceof Error ? indexError.message : 'Readwise index failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ImportCardShell
      icon="📚"
      title="Readwise"
      description="Save a Readwise export token in Vault, then pull your library metadata into SourceIndex."
      badge={savedKeyPresent ? 'Saved key' : undefined}
      footer="This is an API-key flow backed by Vault. It is not an OAuth connector."
    >
      <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-gray-500">
        Readwise API token
        <input
          type="password"
          value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
          className={FIELD_CLASSNAME}
          placeholder={savedKeyPresent ? 'Leave blank to reuse saved token' : 'Paste token'}
        />
      </label>
      <button
        type="button"
        onClick={() => void handleIndex()}
        disabled={busy}
        className="mt-3 rounded border border-[#39FF14]/30 bg-[#39FF14]/10 px-3 py-2 text-xs font-mono text-[#39FF14] transition-colors hover:bg-[#39FF14]/20 disabled:opacity-50"
      >
        {busy ? 'Working...' : savedKeyPresent ? 'Index Readwise' : 'Save Token and Index'}
      </button>
      <ImportStatus error={error} success={success} />
    </ImportCardShell>
  );
}

function GedcomImportCard() {
  return (
    <FileImportCard
      icon="🌳"
      title="GEDCOM"
      description="Upload a GEDCOM family tree export and map it into genealogy source data."
      accept=".ged,.gedcom,text/plain"
      buttonLabel="Upload GEDCOM"
      testId="gedcom-import-input"
      onImport={async (file) => {
        const result = await api.imports.gedcom(file);
        return summarizeGedcomResult(result);
      }}
      footer="Living persons are filtered by the backend during import."
    />
  );
}

function GraphJsonImportCard({
  sourceType,
  title,
  icon,
}: {
  sourceType: 'roam' | 'logseq';
  title: string;
  icon: string;
}) {
  return (
    <FileImportCard
      icon={icon}
      title={title}
      description={`Upload a ${title} JSON export and index its pages, tags, and backlinks.`}
      accept=".json,application/json"
      buttonLabel={`Upload ${title} JSON`}
      testId={`${sourceType}-import-input`}
      onImport={async (file) => {
        const jsonData = await parseJsonFile(file);
        const result =
          sourceType === 'roam'
            ? await api.imports.roam(jsonData)
            : await api.imports.logseq(jsonData);
        return summarizeIndexResult(result);
      }}
      footer="JSON is parsed client-side first so invalid exports fail fast."
    />
  );
}

function ObsidianImportCard() {
  const desktopRuntime = isTauriDesktopRuntime();
  const [vaultPath, setVaultPath] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handlePickFolder = async () => {
    if (!desktopRuntime) {
      setError('Folder picker is only available inside the desktop app.');
      setSuccess(null);
      return;
    }

    try {
      // Tauri dialog not available in Seitiate+ web — use text input fallback
      const selected: string | null = null;
      if (selected && typeof selected === 'string') {
        setVaultPath(selected);
        setError(null);
      }
    } catch (pickError) {
      setError(
        pickError instanceof Error
          ? pickError.message
          : 'Failed to open the folder picker.'
      );
      setSuccess(null);
    }
  };

  const handleIndex = async () => {
    const trimmedPath = vaultPath.trim();
    if (!trimmedPath) {
      setError('Enter a vault path first.');
      setSuccess(null);
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await api.imports.obsidian(trimmedPath);
      setSuccess(`Obsidian indexed. ${summarizeIndexResult(result)}`);
    } catch (indexError) {
      setError(indexError instanceof Error ? indexError.message : 'Obsidian index failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ImportCardShell
      icon="🧠"
      title="Obsidian"
      description="Index a local Obsidian vault by path. This is a local-path flow, not a browser upload."
      badge={desktopRuntime ? 'Desktop ready' : 'Local path'}
      footer="Honest constraint: the backend must be able to read this path. The desktop app can pick a local folder; web only works for self-hosted/local backends."
    >
      <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-gray-500">
        Vault path
        <input
          value={vaultPath}
          onChange={(event) => setVaultPath(event.target.value)}
          className={FIELD_CLASSNAME}
          placeholder="/Users/you/Documents/Obsidian Vault"
        />
      </label>
      <div className="mt-3 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void handleIndex()}
          disabled={busy}
          className="rounded border border-[#39FF14]/30 bg-[#39FF14]/10 px-3 py-2 text-xs font-mono text-[#39FF14] transition-colors hover:bg-[#39FF14]/20 disabled:opacity-50"
        >
          {busy ? 'Working...' : 'Index Vault'}
        </button>
        {desktopRuntime ? (
          <button
            type="button"
            onClick={() => void handlePickFolder()}
            className="rounded border border-gray-700 px-3 py-2 text-xs font-mono text-gray-300 transition-colors hover:border-gray-600 hover:text-gray-100"
          >
            Choose Folder
          </button>
        ) : null}
      </div>
      <ImportStatus error={error} success={success} />
    </ImportCardShell>
  );
}

function MediumExportImportCard() {
  return (
    <FileImportCard
      icon="✍️"
      title="Medium Export"
      description="Upload the ZIP archive from Medium's GDPR export and index bookmarks, highlights, claps, and authored posts."
      accept=".zip,application/zip"
      buttonLabel="Upload Medium ZIP"
      testId="medium-export-input"
      onImport={async (file) => {
        const result = await api.imports.mediumExport(file);
        return summarizeIndexResult(result);
      }}
      footer="Use the archive from Medium Settings -> Download your information."
    />
  );
}

function MediumRssImportCard() {
  const [feedUrls, setFeedUrls] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleIndex = async () => {
    const urls = feedUrls
      .split(/\n|,/)
      .map((value) => value.trim())
      .filter(Boolean);

    if (urls.length === 0) {
      setError('Enter at least one Medium RSS feed URL.');
      setSuccess(null);
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await api.imports.mediumRss(urls);
      setSuccess(`Medium RSS indexed. ${summarizeIndexResult(result)}`);
    } catch (indexError) {
      setError(indexError instanceof Error ? indexError.message : 'Medium RSS index failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ImportCardShell
      icon="📰"
      title="Medium RSS"
      description="Index Medium author, publication, or tag feeds from one or more RSS URLs."
      footer="Paste one feed per line. Example: https://medium.com/feed/@username"
    >
      <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-gray-500">
        Feed URLs
        <textarea
          value={feedUrls}
          onChange={(event) => setFeedUrls(event.target.value)}
          className={`${FIELD_CLASSNAME} min-h-[110px]`}
          placeholder={'https://medium.com/feed/@username\nhttps://medium.com/feed/tag/design'}
        />
      </label>
      <button
        type="button"
        onClick={() => void handleIndex()}
        disabled={busy}
        className="mt-3 rounded border border-[#39FF14]/30 bg-[#39FF14]/10 px-3 py-2 text-xs font-mono text-[#39FF14] transition-colors hover:bg-[#39FF14]/20 disabled:opacity-50"
      >
        {busy ? 'Working...' : 'Index RSS Feeds'}
      </button>
      <ImportStatus error={error} success={success} />
    </ImportCardShell>
  );
}

function MediumArticlesImportCard() {
  const [username, setUsername] = useState('');
  const [rapidApiKey, setRapidApiKey] = useState('');
  const [maxResults, setMaxResults] = useState('100');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleIndex = async () => {
    const normalizedUsername = username.trim().replace(/^@/, '');
    if (!normalizedUsername) {
      setError('Enter a Medium username.');
      setSuccess(null);
      return;
    }

    const parsedMaxResults = Number.parseInt(maxResults, 10);
    if (Number.isNaN(parsedMaxResults) || parsedMaxResults <= 0) {
      setError('Max results must be a positive number.');
      setSuccess(null);
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await api.imports.mediumArticles({
        username: normalizedUsername,
        rapidapi_key: rapidApiKey.trim() || undefined,
        max_results: parsedMaxResults,
      });
      setSuccess(`Medium articles indexed. ${summarizeIndexResult(result)}`);
    } catch (indexError) {
      setError(indexError instanceof Error ? indexError.message : 'Medium API import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ImportCardShell
      icon="🧾"
      title="Medium Articles"
      description="Fetch a Medium user's published articles through the RapidAPI-backed unofficial API."
      footer="RapidAPI key is optional when the platform key is configured; otherwise provide your own key."
    >
      <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-gray-500">
        Username
        <input
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          className={FIELD_CLASSNAME}
          placeholder="@username"
        />
      </label>
      <label className="mt-3 block text-[11px] font-mono uppercase tracking-[0.12em] text-gray-500">
        RapidAPI key
        <input
          type="password"
          value={rapidApiKey}
          onChange={(event) => setRapidApiKey(event.target.value)}
          className={FIELD_CLASSNAME}
          placeholder="Optional if platform key exists"
        />
      </label>
      <label className="mt-3 block text-[11px] font-mono uppercase tracking-[0.12em] text-gray-500">
        Max results
        <input
          value={maxResults}
          onChange={(event) => setMaxResults(event.target.value)}
          className={FIELD_CLASSNAME}
          inputMode="numeric"
        />
      </label>
      <button
        type="button"
        onClick={() => void handleIndex()}
        disabled={busy}
        className="mt-3 rounded border border-[#39FF14]/30 bg-[#39FF14]/10 px-3 py-2 text-xs font-mono text-[#39FF14] transition-colors hover:bg-[#39FF14]/20 disabled:opacity-50"
      >
        {busy ? 'Working...' : 'Index Articles'}
      </button>
      <ImportStatus error={error} success={success} />
    </ImportCardShell>
  );
}

function PlannedImportCard({
  icon,
  title,
  reason,
}: {
  icon: string;
  title: string;
  reason: string;
}) {
  return (
    <ImportCardShell
      icon={icon}
      title={title}
      description={reason}
      badge="Planned"
      footer="This import will become available once the backend endpoint is built."
    >
      <div
        className="rounded px-3 py-2 text-xs"
        style={{
          border: '1px solid color-mix(in srgb, var(--theme-warning) 20%, transparent)',
          background: 'color-mix(in srgb, var(--theme-warning) 8%, transparent)',
          color: 'var(--theme-warning)',
        }}
      >
        Backend endpoint not yet available.
      </div>
    </ImportCardShell>
  );
}

export const ConnectorsPanel: React.FC<{ mode: 'admin' | 'tenant' }> = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const fetchConnections = useCallback(async () => {
    setLoadingConnections(true);
    setConnectionsError(null);
    try {
      const [catalogResponse, enabledResponse] = await Promise.all([
        fetch(`${API_V1}/sources/catalog`, { headers: buildEngineHeaders() }),
        fetch(`${API_V1}/sources/enabled`, { headers: buildEngineHeaders() }),
      ]);
      if (!catalogResponse.ok) {
        throw new Error(`HTTP ${catalogResponse.status}`);
      }

      const catalog = (await catalogResponse.json()) as SourceCatalogEntry[];
      const enabled = enabledResponse.ok
        ? ((await enabledResponse.json()) as EnabledSourceEntry[])
        : [];
      const enabledBySlug = new Map(enabled.map((entry) => [entry.slug, entry]));

      const mappedConnections: Connection[] = catalog.map((entry) => {
        const enabledEntry = enabledBySlug.get(entry.slug);
        return {
          source_type: entry.slug,
          connection_purpose: 'source',
          status: enabledEntry?.connection_status || entry.status || 'not_connected',
          connected_account_name: enabledEntry?.external_username || undefined,
          connected_at: enabledEntry?.last_sync_at || undefined,
          category: CONNECTOR_META[entry.slug]?.category || entry.category || 'Other',
        };
      });

      setConnections(mappedConnections);
    } catch (err) {
      setConnectionsError(err instanceof Error ? err.message : 'Failed to load connectors');
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  useEffect(() => {
    void fetchConnections();
  }, [fetchConnections]);

  const initiateOAuth = async (provider: string) => {
    setActionInProgress(provider);
    try {
      const response = await fetch(`${API_V1}/sources/enable/${provider}?return_to=${encodeURIComponent('/settings')}`, {
        method: 'POST',
        headers: buildEngineHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data.authorize_url) {
        window.open(data.authorize_url, '_blank', 'width=600,height=700');
      }
      await fetchConnections();
    } catch {
      // Existing OAuth surfaces rely on provider popup/error handling.
    } finally {
      setActionInProgress(null);
    }
  };

  const disconnect = async (sourceType: string) => {
    setActionInProgress(sourceType);
    try {
      await fetch(`${API_V1}/sources/disable/${sourceType}`, {
        method: 'DELETE',
        headers: buildEngineHeaders(),
      });
      await fetchConnections();
    } catch {
      // Keep current card state if disconnect fails.
    } finally {
      setActionInProgress(null);
    }
  };

  const sourceConnections = useMemo(
    () => connections.filter((connection) => connection.connection_purpose === 'source'),
    [connections]
  );

  const groupedConnections = useMemo(() => {
    const grouped: Record<string, Connection[]> = {};
    sourceConnections.forEach((connection) => {
      const meta = CONNECTOR_META[connection.source_type];
      const category = connection.category || meta?.category || 'Other';
      grouped[category] = grouped[category] || [];
      grouped[category].push(connection);
    });
    return grouped;
  }, [sourceConnections]);

  return (
    <div className="space-y-6">
      <MacSessionReadbackCard />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
            Connectors :: Data Sources
          </h3>
          <span className="text-[10px] font-mono text-gray-500">
            {sourceConnections.filter((connection) => connection.status === 'connected').length} connected
          </span>
        </div>

        {loadingConnections ? (
          <div className="rounded border border-gray-800 bg-gray-900/20 px-4 py-3 text-sm font-mono text-gray-500 animate-pulse">
            Loading OAuth and direct source connectors...
          </div>
        ) : connectionsError ? (
          <div className="rounded border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            Source connector statuses could not be loaded: {connectionsError}
          </div>
        ) : sourceConnections.length === 0 ? (
          <div className="rounded border border-gray-800 bg-gray-900/20 px-4 py-3 text-sm text-gray-400">
            No source connections are currently installed for this tenant.
          </div>
        ) : (
          CATEGORY_ORDER.map((category) => {
            const categoryConnections = groupedConnections[category];
            if (!categoryConnections || categoryConnections.length === 0) {
              return null;
            }

            return (
              <div key={category}>
                <div className="mb-2 text-[10px] font-mono uppercase tracking-wider text-gray-500">
                  {category}
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {categoryConnections.map((connection) => (
                    <ConnectorCard
                      key={`${connection.source_type}-${connection.connection_purpose}`}
                      connection={connection}
                      onConnect={() => void initiateOAuth(connection.source_type)}
                      onDisconnect={() => void disconnect(connection.source_type)}
                      actionInProgress={actionInProgress === connection.source_type}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
            Lane 2 :: Imports And Manual Sources
          </h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-gray-500">
            Honest surfaces only
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <CsvImportCard />
          <ConversationImportCard />
          <ReadwiseImportCard />
          <GedcomImportCard />
          <GraphJsonImportCard sourceType="roam" title="Roam" icon="🕸️" />
          <GraphJsonImportCard sourceType="logseq" title="Logseq" icon="📓" />
          <ObsidianImportCard />
          <MediumExportImportCard />
          <MediumRssImportCard />
          <MediumArticlesImportCard />
          <PlannedImportCard
            icon="🧬"
            title="23andMe"
            reason="The analysis skill exists, but there is no browser-facing upload/import endpoint for raw 23andMe files yet."
          />
          <PlannedImportCard
            icon="⌨️"
            title="CLI Transcript"
            reason="CLI adapters exist, but there is no dedicated transcript import API for paste/upload into this surface yet."
          />
        </div>
      </div>
    </div>
  );
};

const ConnectorCard: React.FC<{
  connection: Connection;
  onConnect: () => void;
  onDisconnect: () => void;
  actionInProgress: boolean;
}> = ({ connection, onConnect, onDisconnect, actionInProgress }) => {
  const meta = CONNECTOR_META[connection.source_type] || {
    icon: '?',
    label: connection.source_type,
    category: 'Other',
  };
  const isConnected = !['not_connected', 'coming_soon'].includes(connection.status);
  const isComingSoon = connection.status === 'coming_soon';

  return (
    <div
      className={`flex items-center gap-3 rounded border border-gray-800 bg-gray-900/20 p-3 ${
        isComingSoon ? 'opacity-40' : ''
      }`}
    >
      <span className="w-6 flex-shrink-0 text-center text-lg">{meta.icon}</span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-mono text-gray-300">{meta.label}</div>
        {isConnected && connection.connected_account_name ? (
          <div className="truncate text-[10px] text-gray-500">{connection.connected_account_name}</div>
        ) : null}
        <StatusBadge status={connection.status} />
      </div>
      <div className="flex-shrink-0">
        {isComingSoon ? null : isConnected ? (
          <button
            onClick={onDisconnect}
            disabled={actionInProgress}
            className="text-[10px] font-mono text-red-400 hover:underline disabled:opacity-40"
          >
            {actionInProgress ? '...' : 'Disconnect'}
          </button>
        ) : (
          <button
            onClick={onConnect}
            disabled={actionInProgress}
            className="rounded border border-[#39FF14]/30 bg-[#39FF14]/10 px-2 py-0.5 text-[10px] font-mono text-[#39FF14] transition-colors hover:bg-[#39FF14]/20 disabled:opacity-40"
          >
            {actionInProgress ? '...' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { label: string; color: string }> = {
    connected: { label: 'Connected', color: 'text-[#39FF14]' },
    active: { label: 'Active', color: 'text-[#39FF14]' },
    pending_auth: { label: 'Pending Auth', color: 'text-amber-400' },
    oauth_required: { label: 'OAuth Required', color: 'text-amber-400' },
    tools_unavailable: { label: 'Tools Pending', color: 'text-amber-400' },
    not_connected: { label: 'Not Connected', color: 'text-gray-500' },
    coming_soon: { label: 'Coming Soon', color: 'text-blue-400' },
    error: { label: 'Error', color: 'text-red-400' },
    skipped: { label: 'Skipped', color: 'text-gray-600' },
  };
  const { label, color } = config[status] || { label: status, color: 'text-gray-500' };

  return <span className={`text-[9px] font-mono uppercase ${color}`}>{label}</span>;
};
